import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import type { CodeStepLayoutProps } from './SignInLayout.types';
import { SignInTextLink } from './SignInTextLink';

/**
 * The auth card's single-field steps — web and mobile (Figma "UX/UI WebApp"
 * `pKO9bydKQ3f8mJgggb6Idv`, frame 30000:39591 "Verify").
 *
 * Twin of {@link SignInLayout}: it owns the ORDER and the SPACING of the step
 * and nothing else. Every step after the sign-in form goes through it — the
 * emailed sign-in code, the sign-up verification code, the password reset
 * (which fills `extra` with its new-password field) and mobile's "where do we
 * send the reset code" step (an email field in `input`, no resend) — so they
 * cannot end up with four different rhythms.
 *
 * Vertical rhythm, top to bottom (Figma, in DS points):
 *
 *   header                     (Ka tile · title · "We sent a 6-digit code to …")
 *     ↓ 36
 *   input       the six joined cells, centred (or one full-width field)
 *     ↓ 16      extra — ONLY when passed (the reset step's new password)
 *     ↓ 36
 *   action      the 48px primary CTA
 *     ↓ 8
 *   resend      the centred cooldown line, drawn BY this block
 *
 * The resend line is the block's own, not a slot, because its two states are
 * the decision that kept being re-made: while the cooldown runs it is NOT a
 * button at all (a `Text` in `primary-400`, nothing announced as pressable),
 * and only once it is ready does it become a link in `primary-600`.
 *
 * It is OPTIONAL — omit `resend` and no line is rendered at all, the same
 * absent-not-disabled rule the sign-in card's "Forgot password?" follows. A
 * line that mimics the others but does nothing is worse than no line.
 *
 * There is no "Back" row: back is the card's top-left icon button on web and
 * the sheet's own back arrow on mobile.
 */
export function CodeStepLayout({
  header,
  input,
  extra,
  action,
  resend,
  className,
  testID,
}: CodeStepLayoutProps) {
  return (
    <View className={cn('w-full', className)} testID={testID}>
      {header}

      {/* `items-center` so the cells (which centre themselves) and any other
          input a caller passes both land on the card's axis; a `w-full` field
          still stretches. */}
      <View className="mt-9 w-full items-center">{input}</View>

      {extra ? <View className="mt-4 w-full">{extra}</View> : null}

      <View className="mt-9 w-full">{action}</View>

      {resend ? (
        <View className="mt-2 w-full items-center">
          {resend.ready ? (
            <SignInTextLink
              label={resend.label}
              onPress={resend.onPress}
              disabled={resend.disabled}
            />
          ) : (
            <Text className="text-center text-b2 font-medium text-primary-400 native:font-mona-medium">
              {resend.label}
            </Text>
          )}
        </View>
      ) : null}
    </View>
  );
}
(CodeStepLayout as { displayName?: string }).displayName = 'CodeStepLayout';
